<template>
  <div class="form">
    <registration-error v-if="submissionError">
      <span v-html="submissionError" />
    </registration-error>

    <input
      :value="name"
      type="text"
      placeholder="Full Name"
      required
      :class="[
        'form-control',
        errors.name ? 'validation-error' : ''
      ]"
      @keyup.enter="submit"
      @input="setName($event.target.value)"
    >
    <div
      v-if="errors.name"
      class="text-danger"
    >
      <small
        v-for="(error, index) in errors.name"
        :key="index"
      >
        {{ error.message }}<br>
      </small>
    </div>

    <input
      :value="email"
      type="email"
      placeholder="Email Address"
      required
      class="form-control"
      :class="{
        'validation-error': errors.email,
        'agency-tip-border': showAgencyTip,
      }"
      @keyup.enter="submit"
      @keyup="checkForAgency"
      @input="setEmail($event.target.value)"
    >
    <div
      v-if="showAgencyTip"
      class="agency-tip"
    >
      Are you a marketing agency? If so, registering for a 'Hike Agency' account would be better suited to your needs.
      <a
        href="https://my.hike.marketing/register/reseller"
      >Click here to register as an agency.</a>
    </div>
    <div
      v-if="errors.email"
      class="text-danger"
    >
      <small
        v-for="(error, index) in errors.email"
        :key="index"
      >
        {{ error.message }}<br>
      </small>
    </div>

    <input
      :value="password"
      type="password"
      placeholder="Password"
      required
      :class="[
        'form-control',
        errors.password ? 'validation-error' : ''
      ]"
      @keyup.enter="submit"
      @input="setPassword($event.target.value)"
    >

    <password-strength
      :password="password"
      @password-strength="setPasswordStrength"
    />

    <div
      v-if="errors.password"
      class="text-danger"
    >
      <small
        v-for="(error, index) in errors.password"
        :key="index"
      >
        {{ error.message }}<br>
      </small>
    </div>

    <custom-multiselect
      v-model="selectedOperationCountry"
      class="operation_country"
      :options="countries"
      :close-on-select="true"
      :use-short-debounce="false"
    />
    <div
      v-if="errors.operationCountry"
      class="text-danger"
    >
      <small
        v-for="(error, index) in errors.operationCountry"
        :key="index"
      >
        {{ error.message }}<br>
      </small>
    </div>

    <custom-button
      :disabled="preview"
      :loading="loading"
      class="form-control"
      @click="submit"
    >
      Next Step
    </custom-button>
  </div>
</template>


<script>
import CustomButton from '../components/CustomButton.vue';
import RegistrationError from '../components/RegistrationError.vue';
import {mapActions, mapGetters} from "vuex";
import _ from "lodash";
import CustomMultiselect from "../../../reports/components/global/forms/CustomMultiselect";
import PasswordStrength from '../components/PasswordStrength.vue';
import agenciesRegistrationData from '@/data/agencies.json';

export default {
  components: {
    CustomMultiselect,
    CustomButton,
    RegistrationError,
    PasswordStrength,
  },
  props: {
    countries: {
      type: Object,
      required: true,
    },
    merchant: {
      type: Boolean,
      default: false,
      required: false,
    },
    preview: {
      type: Boolean,
      required: false,
      default: false,
    },
    isReseller: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      submissionError: null,
      passwordStrength: null,
      showAgencyTip: false,
    }
  },
  computed: {
    ...mapGetters('registration', [
      'errors',
      'name',
      'email',
      'operationCountry',
      'password',
    ]),
    selectedOperationCountry: {
      get() {
        return this.operationCountry;
      },
      set(value) {
        const { countries } = this;
        this.setOperationCountry({value, countries});
      }
    },
  },
  methods: {
    ...mapActions('registration', [
      'setName',
      'setEmail',
      'setPassword',
      'setOperationCountry',
      'submitCustomerForm',
      'setErrors',
      'setStage',
    ]),

    ...mapGetters('registration', [
        'configuration'
    ]),

    checkForAgency: _.debounce(function (event) {
        this.showAgencyTip =
            agenciesRegistrationData.registration.terms.some(term => event.target.value.includes(term))
            && !this.configuration().whitelabel
            && !this.isReseller;
    }, 1000),

    setPasswordStrength(strength) {
      this.passwordStrength = strength == 100;
    },

    submit()
    {
      if (this.passwordStrength === false) {
        this.submissionError = 'Your password is not strong enough. Please try again.';
        return;
      }

      this.loading = true;
      this.submitCustomerForm(this.countries)
        .then(() => {
          this.setStage('PAYMENT_INFORMATION');
        })
        .catch(response => {
          this.submissionError = 'Sorry, there seems to be an error with your submission. Check the highlighted '+
            'fields and try again.';

          if (response.status === 422) {
            const { data } = response;
            const { error } = data;

            let stateErrors = {};

            if (error) {
              _.forEach(error,(value, key) => {
                stateErrors[key] = [...value.map(message => {
                  return { message: message };
                })];
              });

              this.setErrors(stateErrors);
            }
          }

          if (response.status === 500) {
            this.submissionError = 'There was an unknown error with your submission, please contact '+
              'us for more information.';
          }
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
}
</script>

<style lang="scss" scoped>

/* TODO: Implement bootstrap properly */
.form-control {
  width: 100%;
  padding: 1rem;

  background: #FFFFFF;
  border: 1px solid #D8D8D8;
  box-sizing: border-box;
  border-radius: 4px;

  &.validation-error {
    border: 1px solid #dc3545;
  }
}

.text-danger {
  color: #dc3545;
  margin-top: -14px;
  font-size: 14px;
}

.form-input-group select {
  -moz-appearance: none;
  background: transparent;
  background-image: url("data:image/svg+xml;utf8,<svg fill='%23848484' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
  background-repeat: no-repeat;
  background-position-x: 99%;
  background-position-y: 50%;
}

.btn-register {
  background-color: #4CE0B3;
  color: white;
  text-align: center;
  border: none;
  cursor: pointer;
  font-size: inherit;
  transition: background-color 0.2s ease;

  &:disabled {
    opacity: 0.3;
    cursor: pointer;
  }
}

.btn-register:hover {
  background-color: #3BB390;
}

.operation_country.custom_input {
  :deep(.multiselect) {
    background: #FFFFFF;
    border: 1px solid #D8D8D8;
    box-sizing: border-box;
    border-radius: 4px;

    .multiselect-search,
    .multiselect-single-label,
    .multiselect-placeholder {
      padding: 1rem;
    }

    .multiselect-placeholder {
      color: grey;
    }

    .caret {
      z-index: 1000;
      margin-right: 10px;
    }
  }
}

.agency-tip {
  font-size: 16px;
  color: #1893ae;

  a {
    color: #1893ae;
  }
}

.agency-tip-border {
  border-color: #1893ae;
}
</style>
